import type { CatalogInvestigationRow, ImportRowDraft } from '@/modules/lab/catalog/types'

export function normalizeCatalogText(value: string): string {
  return value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const ABBREVIATION_ALIASES: Record<string, string> = {
  cbp: 'cbc',
  'complete haemogram': 'complete blood count',
  'complete hemogram': 'complete blood count',
  a1c: 'hba1c',
  'glycated haemoglobin': 'hba1c',
  // OPS-PILOT-001A — Nayadupeta sheet normalization
  sgot: 'sgot ast',
  ast: 'sgot ast',
  sgpt: 'sgpt alt',
  alt: 'sgpt alt',
  'serum cretinine': 'serum creatinine',
  'serum total portein': 'serum total protein',
  'serum alkaline phosphatese': 'serum alkaline phosphatase',
  '7tsh': 'serum tsh',
  'hbs ag': 'hbsag',
  'hbs.ag': 'hbsag',
  'hiv 1 2': 'hiv 1 2',
  'rh type': 'rh typing',
  grouping: 'blood grouping',
  'cross matching': 'cross matching',
  'lipid profile': 'lipid profile',
  'l f t': 'liver function test lft',
  'serum electrolytes': 'serum electrolytes panel',
  'fasting sugar': 'fasting blood sugar',
  'post prandial sugar': 'post prandial blood sugar',
  'random sugar': 'random blood sugar',
  'oral gtt': 'oral glucose tolerance test ogtt',
  'pregnency test': 'urine pregnancy test',
  'srcub typos': 'scrub typhus',
  'grams stain': 'gram stain',
  'occuit blood': 'stool occult blood benzidine',
}

export type MatchCandidate = {
  masterId: string
  code: string
  name: string
  score: number
  basis: string
  category?: string
  specimenType?: string | null
  activeFlag?: string
  isLocal?: boolean
  isDraft?: boolean
  hospitalEnabled?: boolean
  aliases?: string[]
}

export type DedupQuery = {
  name?: string
  aliases?: string[]
  shortCode?: string
}

function trigramSet(value: string): Set<string> {
  const n = normalizeCatalogText(value).replace(/\s/g, '')
  const out = new Set<string>()
  if (n.length === 0) return out
  if (n.length <= 2) {
    out.add(n)
    return out
  }
  for (let i = 0; i <= n.length - 3; i++) out.add(n.slice(i, i + 3))
  return out
}

export function trigramSimilarity(a: string, b: string): number {
  const A = trigramSet(a)
  const B = trigramSet(b)
  if (A.size === 0 || B.size === 0) return 0
  let inter = 0
  for (const t of A) {
    if (B.has(t)) inter++
  }
  return (2 * inter) / (A.size + B.size)
}

function rowToCandidate(
  row: CatalogInvestigationRow,
  score: number,
  basis: string,
): MatchCandidate {
  return {
    masterId: row.investigationMasterId,
    code: row.investigationCode,
    name: row.displayName,
    score,
    basis,
    category: row.category,
    specimenType: row.specimenType,
    activeFlag: row.activeFlag,
    isLocal: row.isLocal,
    isDraft: row.isDraft,
    hospitalEnabled: row.hospitalEnabled,
    aliases: row.aliases,
  }
}

function scoreRowAgainstTerm(
  row: CatalogInvestigationRow,
  rawCode: string,
  rawName: string,
): MatchCandidate | null {
  const codeNorm = normalizeCatalogText(rawCode)
  const nameNorm = normalizeCatalogText(ABBREVIATION_ALIASES[rawName.toLowerCase()] ?? rawName)
  const codeAlias = ABBREVIATION_ALIASES[codeNorm] ?? codeNorm
  const rowCode = normalizeCatalogText(row.investigationCode)
  const rowName = normalizeCatalogText(row.displayName)
  const aliasNorms = row.aliases.map((a) => normalizeCatalogText(ABBREVIATION_ALIASES[a.toLowerCase()] ?? a))

  if (codeNorm && (rowCode === codeNorm || rowCode === codeAlias)) {
    return rowToCandidate(row, 1, 'CODE')
  }

  for (const alias of aliasNorms) {
    if (codeNorm && alias === codeNorm) return rowToCandidate(row, 0.99, 'SYNONYM')
    if (nameNorm && alias === nameNorm) return rowToCandidate(row, 0.97, 'SYNONYM')
    if (nameNorm && (alias.includes(nameNorm) || nameNorm.includes(alias))) {
      return rowToCandidate(row, 0.9, 'SYNONYM')
    }
  }

  if (nameNorm && rowName === nameNorm) {
    return rowToCandidate(row, 0.98, 'NAME')
  }

  if (nameNorm && (rowName.includes(nameNorm) || nameNorm.includes(rowName))) {
    return rowToCandidate(row, 0.85, 'TOKEN')
  }

  const tri = trigramSimilarity(nameNorm, rowName)
  if (tri >= 0.82) {
    return rowToCandidate(row, 0.8 + tri * 0.18, 'TRIGRAM')
  }

  const nameTokens = new Set(nameNorm.split(' ').filter((t) => t.length > 2))
  const rowTokens = rowName.split(' ').filter((t) => t.length > 2)
  if (nameTokens.size > 0 && rowTokens.length > 0) {
    const overlap = rowTokens.filter((t) => nameTokens.has(t)).length
    const tokenScore = overlap / Math.max(nameTokens.size, rowTokens.length)
    if (tokenScore >= 0.75) {
      return rowToCandidate(row, 0.75 + tokenScore * 0.15, 'FUZZY')
    }
  }

  return null
}

/** Returns up to `limit` dedup matches sorted by score (client-side pilot). */
export function findDedupMatches(
  query: DedupQuery,
  catalog: CatalogInvestigationRow[],
  limit = 5,
): MatchCandidate[] {
  const terms: Array<{ code: string; name: string }> = []
  const name = query.name?.trim() ?? ''
  const shortCode = query.shortCode?.trim() ?? ''
  if (name) terms.push({ code: shortCode, name })
  if (shortCode && !terms.some((t) => t.code === shortCode)) {
    terms.push({ code: shortCode, name: shortCode })
  }
  for (const alias of query.aliases ?? []) {
    const a = alias.trim()
    if (a) terms.push({ code: '', name: a })
  }

  const byMaster = new Map<string, MatchCandidate>()
  for (const term of terms) {
    for (const row of catalog) {
      const match = scoreRowAgainstTerm(row, term.code, term.name)
      if (!match) continue
      const prev = byMaster.get(match.masterId)
      if (!prev || match.score > prev.score) byMaster.set(match.masterId, match)
    }
  }

  const sorted = [...byMaster.values()].sort((a, b) => b.score - a.score)

  // Surface weak matches for short tokens (e.g. CBC, LFT)
  const primary = sorted.filter((m) => m.score >= 0.72)
  const pool = primary.length > 0 ? primary : sorted.filter((m) => m.score >= 0.55)

  return pool.slice(0, limit)
}

export function scoreMatch(
  rawCode: string,
  rawName: string,
  catalog: CatalogInvestigationRow[],
): MatchCandidate | null {
  return findDedupMatches({ name: rawName, shortCode: rawCode }, catalog, 1)[0] ?? null
}

export function buildImportRows(
  parsed: Array<{ code: string; name: string; category: string }>,
  catalog: CatalogInvestigationRow[],
): ImportRowDraft[] {
  return parsed.map((row, index) => {
    const match = scoreMatch(row.code, row.name, catalog)
    let matchStatus: ImportRowDraft['matchStatus'] = 'NEW'
    let confidence = 0
    let resolution: ImportRowDraft['resolution'] = null

    if (match) {
      confidence = match.score
      if (match.score >= 0.98) {
        matchStatus = 'EXACT'
        resolution = 'LINK'
      } else if (match.score >= 0.75) {
        matchStatus = 'FUZZY'
      } else {
        matchStatus = 'NEW'
      }
    }

    return {
      id: `row-${index + 1}`,
      rowNumber: index + 1,
      rawCode: row.code,
      rawName: row.name,
      rawCategory: row.category,
      matchStatus,
      confidence,
      suggestedMasterId: match?.masterId ?? null,
      suggestedCode: match?.code ?? null,
      suggestedName: match?.name ?? null,
      resolution,
    }
  })
}

/** Rows still awaiting an operator decision (not yet LINK / CREATE / SKIP). */
export function importRowsNeedingReview(rows: ImportRowDraft[]): ImportRowDraft[] {
  return rows.filter((r) => {
    if (r.resolution === 'LINK' || r.resolution === 'CREATE' || r.resolution === 'SKIP') {
      return false
    }
    if (r.applyStatus === 'APPLIED' || r.applyStatus === 'SKIPPED') {
      return false
    }
    return (
      r.matchStatus === 'FUZZY' ||
      r.matchStatus === 'CONFLICT' ||
      (r.matchStatus === 'NEW' && r.confidence < 0.75)
    )
  })
}

/** Rows with a saved decision that still need batch apply to hit the catalog. */
export function importRowsPendingApply(rows: ImportRowDraft[]): ImportRowDraft[] {
  return rows.filter(
    (r) =>
      (r.resolution === 'LINK' || r.resolution === 'CREATE') &&
      r.applyStatus !== 'APPLIED' &&
      r.applyStatus !== 'FAILED',
  )
}
